import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import {
  Sparkles,
  Check,
  CreditCard,
  Users,
  Mail,
  Layers,
  Star,
  Pencil,
  X as XIcon,
  Save,
  Rocket,
  CheckCircle2,
  AlertCircle,
  History,
  Globe,
} from "lucide-react";
import { devWarn } from "@/lib/devlog";

const TIER_ACCENT = {
  connect: { ring: "ring-blue-200", chip: "bg-blue-50 text-blue-700", btn: "bg-blue-600 hover:bg-blue-500" },
  grow: { ring: "ring-violet-200", chip: "bg-violet-50 text-violet-700", btn: "bg-violet-600 hover:bg-violet-500" },
  scale: { ring: "ring-amber-200", chip: "bg-amber-50 text-amber-700", btn: "bg-amber-600 hover:bg-amber-500" },
};

const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export default function AdminSubscriptions() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // tier_id or null
  const [publishStatus, setPublishStatus] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/subscriptions/publish-status");
      setPublishStatus(r.data);
    } catch (e) {
      // Non-fatal — status banner just won't render.
      devWarn("publish-status fetch failed", e);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/subscriptions/tiers");
      setTiers(r.data?.tiers || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
    loadStatus();
  }, [loadStatus]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const reset = async () => {
    if (!window.confirm("Reset all tiers to factory defaults? Any custom edits will be lost.")) return;
    try {
      await api.post("/admin/subscriptions/tiers/reset");
      toast.success("Tiers reset to defaults.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publish = async () => {
    const pending = publishStatus?.pending_changes?.length || 0;
    const summary = pending
      ? `Deploy ${pending} pending change${pending === 1 ? "" : "s"} to the public Pricing page now?`
      : "No pending changes detected. Re-deploy the current draft anyway?";
    if (!window.confirm(summary)) return;
    setPublishing(true);
    try {
      const note = window.prompt("Optional note for this deploy (e.g., 'Q3 price update'):", "") || "";
      const r = await api.post("/admin/subscriptions/publish", { note });
      toast.success(`Deployed v${r.data.version} to the Pricing page.`);
      loadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPublishing(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/subscriptions/publish-history");
      setHistory(r.data?.history || []);
      setShowHistory(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Business"
      title="Platform Subscriptions"
      intro="Three flagship SaaS tiers. Foundation (vendor network, programs, workflows, consumers) is included in every tier. Higher tiers layer on marketing, AI, automation, multi-location, and enterprise controls."
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={loadHistory}
            data-testid="admin-tiers-history-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            <History className="w-3.5 h-3.5" /> History
          </button>
          <button
            type="button"
            onClick={reset}
            data-testid="admin-tiers-reset-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            Reset to defaults
          </button>
          <button
            type="button"
            onClick={publish}
            disabled={publishing}
            data-testid="admin-tiers-deploy-btn"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold text-white disabled:opacity-60 ${
              publishStatus?.has_pending_changes
                ? "bg-emerald-600 hover:bg-emerald-500 ring-2 ring-emerald-200"
                : "bg-slate-900 hover:bg-slate-800"
            }`}
            title={publishStatus?.has_pending_changes ? "Pending changes ready to ship" : "Re-deploy current draft"}
          >
            <Rocket className="w-3.5 h-3.5" />
            {publishing ? "Deploying…" : publishStatus?.has_pending_changes ? "Deploy to Pricing page" : "Re-deploy"}
          </button>
        </div>
      }
    >
      <PublishStatusBanner status={publishStatus} />
      {showHistory && (
        <PublishHistoryModal
          history={history}
          onClose={() => setShowHistory(false)}
        />
      )}
      {loading && <div className="text-sm text-slate-500">Loading tiers…</div>}
      {!loading && (
        <div data-testid="admin-tiers-grid" className="grid lg:grid-cols-3 gap-5 items-start">
          {tiers.map((t) => (
            <TierCard
              key={t.id}
              tier={t}
              onEdit={() => setEditing(t.id)}
            />
          ))}
        </div>
      )}

      {editing && (
        <TierEditor
          tier={tiers.find((t) => t.id === editing)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </ToolPageShell>
  );
}

function TierCard({ tier, onEdit }) {
  const accent = TIER_ACCENT[tier.id] || TIER_ACCENT.connect;
  return (
    <div
      data-testid={`admin-tier-card-${tier.id}`}
      className={`relative rounded-2xl border border-slate-200 bg-white p-6 ring-1 ${accent.ring} ${
        tier.highlight ? "shadow-lg" : "shadow-sm"
      }`}
    >
      {tier.highlight && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-violet-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5">
          <Star className="w-3 h-3" /> Most popular
        </span>
      )}
      <div className="flex items-start justify-between">
        <div>
          <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${accent.chip}`}>
            {tier.id}
          </span>
          <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-slate-900">
            {tier.name}
          </h3>
          <p className="mt-1 text-sm text-slate-600">{tier.tagline}</p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          data-testid={`admin-tier-edit-${tier.id}`}
          className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
          title="Edit tier"
        >
          <Pencil className="w-4 h-4" />
        </button>
      </div>

      <div className="border-t border-slate-100 mt-4 pt-4">
        <div className="flex items-baseline gap-1">
          <span className="font-display text-4xl font-extrabold tracking-tight text-slate-900">
            {usd(tier.price_monthly)}
          </span>
          <span className="text-sm text-slate-500">/ month</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          or {usd(tier.price_annual)} / year (save {Math.round((1 - tier.price_annual / (tier.price_monthly * 12)) * 100)}%)
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
        <Limit label="Consumers" value={tier.limits.consumers} />
        <Limit label="Locations" value={tier.limits.locations} />
        <Limit label="Emails / mo" value={tier.limits.marketing_emails_per_month} />
        <Limit label="SMS / mo" value={tier.limits.marketing_sms_per_month} />
        <Limit label="Automations" value={tier.limits.automations} />
        <Limit label="Team seats" value={tier.limits.team_seats} />
      </div>

      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
          Primary features
        </div>
        <ul className="space-y-1.5 text-sm text-slate-700">
          {tier.primary_features.map((f, i) => (
            <li key={`${f}-${i}`} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      {tier.secondary_features?.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
            Secondary
          </div>
          <ul className="space-y-1 text-xs text-slate-600">
            {tier.secondary_features.map((f, i) => (
              <li key={`${f}-${i}`} className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-slate-400 shrink-0 mt-1.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-3 gap-3 text-center">
        <Stat label="Cost" value={usd(tier.cost_total)} />
        <Stat label="Margin" value={usd(tier.gross_margin_usd)} accent="text-emerald-700" />
        <Stat label="Margin %" value={`${tier.gross_margin_pct}%`} />
      </div>
      <div className="mt-3 text-[11px] text-slate-500 italic">
        Target: {tier.target_persona}
      </div>
    </div>
  );
}

function Limit({ label, value }) {
  const display = value === -1 ? "Unlimited" : value === 0 ? "—" : new Intl.NumberFormat().format(value);
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-bold text-slate-900 text-xs">{display}</div>
    </div>
  );
}

function Stat({ label, value, accent = "text-slate-900" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function TierEditor({ tier, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: tier.name,
    tagline: tier.tagline,
    price_monthly: tier.price_monthly,
    price_annual: tier.price_annual,
    primary_features: [...(tier.primary_features || [])],
    secondary_features: [...(tier.secondary_features || [])],
    limits: { ...(tier.limits || {}) },
    target_persona: tier.target_persona,
    highlight: tier.highlight,
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/subscriptions/tiers/${tier.id}`, form);
      toast.success(`${form.name} updated.`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateFeatureList = (key, idx, value) => {
    setForm((f) => {
      const list = [...f[key]];
      list[idx] = value;
      return { ...f, [key]: list };
    });
  };
  const addFeature = (key) => setForm((f) => ({ ...f, [key]: [...f[key], ""] }));
  const removeFeature = (key, idx) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-stretch justify-center overflow-y-auto p-4" data-testid="admin-tier-editor">
      <div className="w-full max-w-2xl my-6 rounded-2xl bg-white shadow-2xl flex flex-col">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-slate-900">Edit tier — {tier.id}</div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Tagline">
            <textarea
              rows={2}
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Monthly price (USD)">
              <input
                type="number"
                value={form.price_monthly}
                onChange={(e) => setForm({ ...form, price_monthly: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Annual price (USD)">
              <input
                type="number"
                value={form.price_annual}
                onChange={(e) => setForm({ ...form, price_annual: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <Field label="Limits">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(form.limits).map(([k, v]) => (
                <label key={k} className="text-xs text-slate-600">
                  <span className="block font-semibold mb-0.5 capitalize">{k.replace(/_/g, " ")} (-1 = unlimited)</span>
                  <input
                    type="number"
                    value={v}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        limits: { ...form.limits, [k]: parseInt(e.target.value) || 0 },
                      })
                    }
                    className="w-full rounded-lg border border-slate-200 px-2 py-1 text-sm"
                  />
                </label>
              ))}
            </div>
          </Field>
          <FeatureListEditor
            label="Primary features"
            items={form.primary_features}
            onUpdate={(i, v) => updateFeatureList("primary_features", i, v)}
            onAdd={() => addFeature("primary_features")}
            onRemove={(i) => removeFeature("primary_features", i)}
          />
          <FeatureListEditor
            label="Secondary features"
            items={form.secondary_features}
            onUpdate={(i, v) => updateFeatureList("secondary_features", i, v)}
            onAdd={() => addFeature("secondary_features")}
            onRemove={(i) => removeFeature("secondary_features", i)}
          />
          <Field label="Target persona">
            <textarea
              rows={2}
              value={form.target_persona}
              onChange={(e) => setForm({ ...form, target_persona: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.highlight}
              onChange={(e) => setForm({ ...form, highlight: e.target.checked })}
            />
            Highlight as "Most popular"
          </label>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-slate-100 hover:bg-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="admin-tier-editor-save"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function FeatureListEditor({ label, items, onUpdate, onAdd, onRemove }) {
  return (
    <Field label={label}>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={`${it}-${i}`} className="flex items-center gap-1.5">
            <input
              type="text"
              value={it}
              onChange={(e) => onUpdate(i, e.target.value)}
              className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="p-1 rounded-full hover:bg-rose-50 text-rose-500"
            >
              <XIcon className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={onAdd}
          className="text-xs font-semibold rounded-full px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700"
        >
          + Add
        </button>
      </div>
    </Field>
  );
}


const REASON_LABEL = {
  never_published: "never published",
  new_tier: "new tier",
  edited: "edited",
  saved_no_diff: "saved",
  removed: "removed",
};

function _fmtDeployedAt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function PublishStatusBanner({ status }) {
  if (!status) return null;
  const pending = status.pending_changes || [];
  const live = status.latest;

  if (!live && pending.length === 0) return null;

  if (status.has_pending_changes) {
    return (
      <div
        data-testid="admin-tiers-publish-banner"
        className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
      >
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-amber-900 text-sm">
            {pending.length} pending change{pending.length === 1 ? "" : "s"} not yet on the Pricing page
          </div>
          <div className="text-xs text-amber-800 mt-1 flex flex-wrap gap-1.5">
            {pending.slice(0, 6).map((p, i) => (
              <span
                key={p.id || `pending-${i}`}
                data-testid={`admin-tiers-pending-${p.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-200 px-2 py-0.5 font-semibold"
              >
                {p.name || p.id}
                <span className="text-amber-700/80">· {REASON_LABEL[p.reason] || p.reason}</span>
                {p.fields?.length ? <span className="text-amber-700/60">({p.fields.join(", ")})</span> : null}
              </span>
            ))}
            {pending.length > 6 && <span className="text-amber-700">+{pending.length - 6} more</span>}
          </div>
          <div className="text-[11px] text-amber-700/80 mt-1.5">
            Click <span className="font-bold">Deploy to Pricing page</span> to ship these changes live to /pricing.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-tiers-publish-banner"
      className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3"
    >
      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-bold text-emerald-900">Pricing page is live</span>
        <span className="text-emerald-800/80"> · v{live?.version} deployed {_fmtDeployedAt(live?.deployed_at)}</span>
        {live?.deployed_by ? <span className="text-emerald-800/70"> · by {live.deployed_by}</span> : null}
      </div>
      <a
        href="/pricing"
        target="_blank"
        rel="noreferrer"
        data-testid="admin-tiers-view-live"
        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-900 hover:underline"
      >
        <Globe className="w-3 h-3" /> View live
      </a>
    </div>
  );
}

function PublishHistoryModal({ history, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="admin-tiers-history-modal"
    >
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="font-display font-bold text-slate-900">Deploy history</div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {history.length === 0 ? (
            <div className="text-sm text-slate-500">No deploys yet.</div>
          ) : (
            <ul className="space-y-2">
              {history.map((h) => (
                <li key={h.id} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-slate-900 text-sm">v{h.version}</div>
                    <div className="text-[11px] text-slate-500">{_fmtDeployedAt(h.deployed_at)}</div>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    by <span className="font-semibold">{h.deployed_by || "—"}</span>
                  </div>
                  {h.note ? <div className="text-xs italic text-slate-500 mt-1">"{h.note}"</div> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
